import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  assignments as assignmentsApi, users as usersApi, stores as storesApi,
  templates as templatesApi, files as filesApi,
} from "../lib/api";
import { localizedTemplate } from "../lib/i18nContent";
import { toast } from "sonner";
import {
  ClipboardCheck, ListTodo, Plus, X, Check, Paperclip, Loader2, Trash2,
} from "lucide-react";

const PRIORITIES = ["low", "medium", "high"];

function blankForm() {
  return {
    auditor_id: "",
    store_id: "",
    categories: [],
    title: "",
    assigned_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    priority: "medium",
    instructions: "",
    comment: "",
    attachments: [],
  };
}

export default function CreateAssignmentDialog({ open, onOpenChange, onCreated }) {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState("store_audit");
  const [auditors, setAuditors] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [items, setItems] = useState([{ text: "" }]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType("store_audit");
    setForm(blankForm());
    setItems([{ text: "" }]);
    Promise.all([usersApi.assignable(), storesApi.list(true), templatesApi.list()])
      .then(([u, s, tpl]) => {
        setAuditors((u || []).filter((x) => x.role === "auditor" || x.role === "gerente"));
        setStoreList(s || []);
        setTemplateList(tpl || []);
      })
      .catch(() => {});
  }, [open]);

  const toggleCategory = (name) =>
    setForm((f) => {
      const set = new Set(f.categories);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      return { ...f, categories: Array.from(set) };
    });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const rec = await filesApi.upload(file, "", "assignment");
        uploaded.push({
          file_id: rec.id,
          name: rec.original_filename,
          url: filesApi.url(rec.id),
          content_type: rec.content_type,
        });
      }
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
    } catch {
      toast.error(t("assignments.toast_error"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (idx) =>
    setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  const setItem = (idx, text) =>
    setItems((its) => its.map((it, i) => (i === idx ? { text } : it)));
  const addItem = () => setItems((its) => [...its, { text: "" }]);
  const removeItem = (idx) => setItems((its) => its.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.auditor_id) return toast.error(t("assignments.need_auditor"));
    if (type === "store_audit") {
      if (!form.store_id) return toast.error(t("assignments.need_store"));
      if (form.categories.length === 0) return toast.error(t("assignments.need_category"));
    } else {
      const clean = items.map((i) => (i.text || "").trim()).filter(Boolean);
      if (clean.length === 0) return toast.error(t("assignments.need_items"));
    }
    setSaving(true);
    try {
      const payload = {
        type,
        auditor_id: form.auditor_id,
        store_id: type === "store_audit" ? form.store_id : "",
        categories: type === "store_audit" ? form.categories : [],
        title: form.title.trim(),
        assigned_date: form.assigned_date,
        due_date: form.due_date,
        priority: form.priority,
        instructions: form.instructions.trim(),
        comment: form.comment.trim(),
        attachments: form.attachments,
      };
      if (type === "task_checklist") {
        payload.checklist_items = items
          .map((i) => ({ text: (i.text || "").trim() }))
          .filter((i) => i.text);
      }
      const created = await assignmentsApi.create(payload);
      toast.success(t("assignments.toast_created"));
      onOpenChange(false);
      onCreated && onCreated(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("assignments.toast_error"));
    } finally {
      setSaving(false);
    }
  };

  const TypeCard = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setType(value)}
      className={`flex-1 text-left rounded-xl border p-4 transition-all ${
        type === value
          ? "border-[#1E3A8A] ring-2 ring-[#1E3A8A]/20 bg-[#E5EBFA]/40"
          : "border-stone-200 hover:border-stone-300"
      }`}
      data-testid={`assign-type-${value}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-lg bg-[#1E3A8A] text-white grid place-items-center">
          <Icon size={16} />
        </span>
        <span className="font-semibold text-sm">{title}</span>
        {type === value && <Check size={16} className="ml-auto text-[#1E3A8A]" />}
      </div>
      <p className="text-[11px] text-stone-500 mt-2">{desc}</p>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="create-assignment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ClipboardCheck size={20} className="text-[#1E3A8A]" />
            {t("assignments.create_title")}
          </DialogTitle>
          <p className="text-sm text-stone-500">{t("assignments.create_subtitle")}</p>
        </DialogHeader>

        <div className="space-y-5 mt-1">
          {/* Type */}
          <div>
            <label className="canales-overline">{t("assignments.choose_type")}</label>
            <div className="flex flex-col sm:flex-row gap-3 mt-1.5">
              <TypeCard
                value="store_audit"
                icon={ClipboardCheck}
                title={t("assignments.choose_type_audit")}
                desc={t("assignments.choose_type_audit_desc")}
              />
              <TypeCard
                value="task_checklist"
                icon={ListTodo}
                title={t("assignments.choose_type_task")}
                desc={t("assignments.choose_type_task_desc")}
              />
            </div>
          </div>

          {/* Auditor */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="canales-overline">{t("assignments.auditor")} *</label>
              <Select value={form.auditor_id} onValueChange={(v) => setForm({ ...form, auditor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="assign-auditor-select">
                  <SelectValue placeholder={t("assignments.auditor")} />
                </SelectTrigger>
                <SelectContent>
                  {auditors.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`assign-auditor-opt-${a.id}`}>
                      {a.name} · {t(`roles.${a.role}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="canales-overline">{t("assignments.priority")}</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="assign-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{t(`assignments.priority_${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Store + categories (store_audit) */}
          {type === "store_audit" && (
            <>
              <div>
                <label className="canales-overline">{t("assignments.store")} *</label>
                <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="assign-store-select">
                    <SelectValue placeholder={t("assignments.store")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {storeList.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`assign-store-opt-${s.id}`}>
                        {s.tienda} · {s.ciudad}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="canales-overline">{t("assignments.choose_categories")} *</label>
                <p className="text-[11px] text-stone-500 mb-2">{t("assignments.choose_categories_hint")}</p>
                <div className="flex flex-wrap gap-2">
                  {templateList.map((tpl) => {
                    const active = form.categories.includes(tpl.name);
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => toggleCategory(tpl.name)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                          active
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
                        }`}
                        data-testid={`assign-cat-${tpl.name}`}
                      >
                        {active && <Check size={11} className="inline mr-1 -mt-0.5" />}
                        {localizedTemplate(tpl.name, i18n.language)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Title + items (task_checklist) */}
          {type === "task_checklist" && (
            <>
              <div>
                <label className="canales-overline">{t("assignments.task_title")}</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1"
                  placeholder={t("assignments.task_title")}
                  data-testid="assign-title-input"
                />
              </div>
              <div>
                <label className="canales-overline">{t("assignments.checklist_items")} *</label>
                <div className="space-y-2 mt-1.5">
                  {items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={it.text}
                        onChange={(e) => setItem(idx, e.target.value)}
                        placeholder={t("assignments.item_ph")}
                        data-testid={`assign-item-input-${idx}`}
                      />
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="p-2 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <X size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="mt-2 gap-1.5 rounded-full"
                  data-testid="assign-add-item"
                >
                  <Plus size={13} /> {t("assignments.add_item")}
                </Button>
              </div>
            </>
          )}

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="canales-overline">{t("assignments.assigned_date")}</label>
              <Input
                type="date"
                value={form.assigned_date}
                onChange={(e) => setForm({ ...form, assigned_date: e.target.value })}
                className="mt-1"
                data-testid="assign-assigned-date"
              />
            </div>
            <div>
              <label className="canales-overline">{t("assignments.due_date")}</label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="mt-1"
                data-testid="assign-due-date"
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label className="canales-overline">{t("assignments.instructions")}</label>
            <Textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              className="mt-1"
              rows={3}
              data-testid="assign-instructions"
            />
          </div>

          {/* Attachments */}
          <div>
            <label className="canales-overline">{t("assignments.attach_files")}</label>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 text-xs font-semibold cursor-pointer hover:border-stone-300">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
                {t("assignments.attach_files")}
                <input type="file" multiple className="hidden" onChange={onUpload} data-testid="assign-attach-input" />
              </label>
              {form.attachments.map((a, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-100 text-xs"
                >
                  {a.name?.slice(0, 22)}
                  <button type="button" onClick={() => removeAttachment(idx)} className="text-stone-400 hover:text-red-600">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button className="brand-button gap-2" onClick={submit} disabled={saving} data-testid="assign-save-button">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t("assignments.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
